import { useEffect, useRef, useState } from 'react';
import './RegisterForm.css';

/*
  Newsletter sign-up form for the AppTeaser section. Same approach as
  ContactForm: it posts straight to a Brevo hosted-form endpoint, so the field
  names (FIRSTNAME, LASTNAME, EMAIL), the honeypot/locale fields and the DOM
  ids/classes (#sib-form, #sib-container, #error-message, #success-message,
  .form__entry, .entry__error) have to match what sibforms' main.js expects.
  See ContactForm.js for the reasoning behind the script-loading effects below.
*/
const BREVO_FORM_ACTION =
  'https://eb1d8f89.sibforms.com/serve/MUIFAClxg7hydxzS01zYTmLojw3LdacByLsnSzQKEpHbQcWASWJbYr-0lPHOcuOckzmUNm3KGckcw4WQgvRmuB8SudAhvrFHxFO75Juv1k7j45boQ3Vg8QLaAdgxcoSz-B_l7bHDrJAQtTmHPfihtiR472-oleZPfruk65rf8CckoK99X6o5LE-3B1wvXAvcv-gG8DHwi3_735lODw==';
const TURNSTILE_SITEKEY = '0x4AAAAAAEtaHTcy0uyFDfUO';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const FieldError = ({ id, message }) => (
  <label id={id} className="entry__error entry__error--primary" role="alert">
    {message}
  </label>
);

function RegisterForm({ onSuccess }) {
  const [errors, setErrors] = useState({});
  const containerRef = useRef(null);

  // Capture-phase on an ancestor so it runs before main.js's own submit
  // listener and can stop invalid submissions from ever being posted.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const validate = (event) => {
      const form = event.target;
      const nextErrors = {};
      if (!EMAIL_PATTERN.test(form.elements.EMAIL.value.trim())) {
        nextErrors.EMAIL = 'Please enter a valid email address.';
      }
      setErrors(nextErrors);

      if (!nextErrors.EMAIL) return;

      event.preventDefault();
      event.stopPropagation();
      form.elements.EMAIL.focus();
    };

    container.addEventListener('submit', validate, true);
    return () => container.removeEventListener('submit', validate, true);
  }, []);

  useEffect(() => {
    window.REQUIRED_CODE_ERROR_MESSAGE = 'Please choose a country code';
    window.LOCALE = 'en';
    window.INVALID_NAME = 'Please enter your name.';
    window.EMAIL_INVALID_MESSAGE = window.SMS_INVALID_MESSAGE =
      'Please enter a valid email address.';
    window.REQUIRED_ERROR_MESSAGE = 'This field cannot be left blank.';
    window.GENERIC_INVALID_MESSAGE =
      'The information provided is invalid. Please review the field and try again.';
    window.INVALID_NUMBER = 'Please enter a valid number.';
    window.AUTOHIDE = true;
    window.handleCaptchaResponse = function handleCaptchaResponse() {
      document.getElementById('sib-captcha')?.dispatchEvent(new Event('captchaChange'));
      window.grecaptcha = window.turnstile;
    };

    const turnstileSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    if (!document.querySelector(`script[src="${turnstileSrc}"]`)) {
      const script = document.createElement('script');
      script.src = turnstileSrc;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }
  }, []);

  // main.js binds to the #sib-form element that exists when it runs, so it's
  // re-executed on every mount (the timeout skips StrictMode's first pass).
  useEffect(() => {
    const src = 'https://sibforms.com/forms/end-form/build/main.js';
    const timer = setTimeout(() => {
      document.querySelectorAll(`script[src="${src}"]`).forEach((old) => old.remove());
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Turnstile only auto-renders widgets present at its first load, so render
  // explicitly when remounting with the script already loaded.
  useEffect(() => {
    const el = document.getElementById('sib-captcha');
    if (!el) return undefined;

    let widgetId;
    if (window.turnstile && !el.hasChildNodes()) {
      widgetId = window.turnstile.render(el, {
        sitekey: TURNSTILE_SITEKEY,
        language: 'en',
        callback: window.handleCaptchaResponse,
      });
    }
    return () => {
      if (widgetId !== undefined) window.turnstile?.remove(widgetId);
    };
  }, []);

  useEffect(() => {
    const successPanel = document.getElementById('success-message');
    if (!successPanel) return undefined;

    const observer = new MutationObserver(() => {
      if (!successPanel.classList.contains('sib-form-message-panel--active')) return;
      if (onSuccess) onSuccess();
    });
    observer.observe(successPanel, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [onSuccess]);

  return (
    <div className="RegisterForm sib-form">
      <div id="sib-form-container" className="sib-form-container">
        <div id="error-message" className="sib-form-message-panel RegisterForm-panel RegisterForm-panel--error">
          <span className="sib-form-message-panel__inner-text">
            Your information could not be saved. Please try again.
          </span>
        </div>

        <div
          id="success-message"
          className="sib-form-message-panel RegisterForm-panel RegisterForm-panel--success"
          role="status"
          tabIndex={-1}
        >
          <span className="sib-form-message-panel__inner-text">
            Thanks for registering! We'll keep you in the loop.
          </span>
        </div>

        <div id="sib-container" ref={containerRef} className="sib-container--large sib-container--vertical">
          <form id="sib-form" method="POST" action={BREVO_FORM_ACTION} data-type="subscription" noValidate>
            <div className="RegisterForm-row">
              <div className="form__entry entry_block RegisterForm-field">
                <label className="entry__label" htmlFor="FIRSTNAME">First Name</label>
                <div className="entry__field">
                  <input
                    className="input"
                    type="text"
                    id="FIRSTNAME"
                    name="FIRSTNAME"
                    maxLength={200}
                    autoComplete="given-name"
                  />
                </div>
                <FieldError />
              </div>

              <div className="form__entry entry_block RegisterForm-field">
                <label className="entry__label" htmlFor="LASTNAME">Last Name</label>
                <div className="entry__field">
                  <input
                    className="input"
                    type="text"
                    id="LASTNAME"
                    name="LASTNAME"
                    maxLength={200}
                    autoComplete="family-name"
                  />
                </div>
                <FieldError />
              </div>
            </div>

            <div className="form__entry entry_block RegisterForm-field">
              <label className="entry__label" htmlFor="EMAIL">Email *</label>
              <div className="entry__field">
                <input
                  className="input"
                  type="email"
                  id="EMAIL"
                  name="EMAIL"
                  autoComplete="email"
                  data-required="true"
                  required
                  aria-invalid={Boolean(errors.EMAIL)}
                  aria-describedby="EMAIL-error"
                  onChange={() => errors.EMAIL && setErrors((e) => ({ ...e, EMAIL: undefined }))}
                />
              </div>
              <FieldError id="EMAIL-error" message={errors.EMAIL} />
            </div>

            <div className="RegisterForm-footer">
              <div className="form__entry entry_block RegisterForm-captcha">
                <div
                  className="cf-turnstile g-recaptcha"
                  data-sitekey={TURNSTILE_SITEKEY}
                  id="sib-captcha"
                  data-callback="handleCaptchaResponse"
                  data-language="en"
                />
                <FieldError />
              </div>

              <button type="submit" className="RegisterForm-submit">
                {/* main.js toggles a loader class on the button's <svg>; it must exist. */}
                <svg className="icon sib-hide-loader-icon" viewBox="0 0 512 512" aria-hidden="true">
                  <path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z" />
                </svg>
                Register
              </button>
            </div>

            <input
              type="text"
              name="email_address_check"
              defaultValue=""
              className="input--hidden"
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />
            <input type="hidden" name="locale" value="en" />
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterForm;
